import React, {
  useState,
  useContext,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/functions";
import { db } from "services/firebase";
import { AuthContext } from "context/Auth";
import { format } from "date-fns";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  FormHelperText,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { LoadingButton } from "@mui/lab";
import { useNavigate } from "react-router-dom";
import DashboardPageHeader from "components/common/DashboardPageHeader";
import ConfirmDialog from "components/common/ConfirmDialog";

const formatTimestamp = (timestamp) =>
  timestamp ? format(timestamp.toDate(), "MMM d, yyyy") : "—";

const ApprovalActions = ({
  loading,
  disabled,
  approveLabel,
  rejectLabel,
  onApprove,
  onReject,
}) => (
  <Box sx={{ display: "flex", gap: 1.5 }}>
    <LoadingButton
      variant="contained"
      disabled={disabled || !!loading}
      loading={loading === "approve"}
      onClick={onApprove}
    >
      {approveLabel}
    </LoadingButton>
    <LoadingButton
      variant="contained"
      color="error"
      disabled={disabled || !!loading}
      loading={loading === "reject"}
      onClick={onReject}
    >
      {rejectLabel}
    </LoadingButton>
  </Box>
);

const ApprovalPage = () => {
  const { userData } = useContext(AuthContext);
  const navigate = useNavigate();

  // Pending clinic locations
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [locationError, setLocationError] = useState();

  // Facility access requests
  const [claims, setClaims] = useState([]);
  const [selectedClaims, setSelectedClaims] = useState([]);
  const [claimsLoaded, setClaimsLoaded] = useState(false);
  const [claimLoading, setClaimLoading] = useState(false);
  const [claimError, setClaimError] = useState();

  const [submitted, setSubmitted] = useState();
  // { kind: "location-approve" | "location-reject" | "claim-approve" | "claim-reject" }
  const [confirm, setConfirm] = useState(null);

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        if (!a.registrationDate) return 1;
        if (!b.registrationDate) return -1;
        return b.registrationDate.toDate() - a.registrationDate.toDate();
      }),
    [rows]
  );

  const handleSelectAllLocations = useCallback(
    (event) => setSelected(event.target.checked ? rows.map((row) => row.id) : []),
    [rows]
  );

  const toggleLocation = useCallback((id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  }, []);

  const handleSelectAllClaims = useCallback(
    (event) =>
      setSelectedClaims(event.target.checked ? claims.map((claim) => claim.id) : []),
    [claims]
  );

  const toggleClaim = useCallback((id) => {
    setSelectedClaims((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      const locationsSnapshot = await db
        .collection("locations")
        .where("status", "==", "pending")
        .get();

      const locations = [];
      const userIds = new Set();

      locationsSnapshot.forEach((doc) => {
        const data = doc.data();
        if (data) {
          locations.push({ ...data, id: doc.id });
          if (data.users) {
            data.users.forEach((userId) => userIds.add(userId));
          }
        }
      });

      if (userIds.size > 0) {
        const usersSnapshot = await db
          .collection("users")
          .where("uid", "in", Array.from(userIds))
          .get();

        const userData = {};
        usersSnapshot.forEach((doc) => {
          const data = doc.data();
          userData[data.uid] = {
            email: data.email,
            registrationDate: data.registrationDate,
          };
        });

        locations.forEach((location) => {
          if (location.users && location.users.length > 0) {
            const firstUser = userData[location.users[0]];
            location.email = location.users
              .map((userId) => userData[userId]?.email)
              .join(", ");
            location.registrationDate = firstUser?.registrationDate || null;
          }
        });
      }

      setRows(locations);
      setDataLoaded(true);
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchClaims = async () => {
      const snap = await db
        .collection("facilityClaims")
        .where("status", "==", "pending")
        .get();
      setClaims(snap.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      setClaimsLoaded(true);
    };

    fetchClaims();
  }, []);

  const getUserEmails = async (ids) => {
    let userIds = [];
    let userEmails = [];

    ids.forEach((id) => {
      const users = rows.find((row) => row.id === id).users;
      userIds.push(...users);
    });

    await db
      .collection("users")
      .where("uid", "in", userIds)
      .get()
      .then((querySnapshot) => {
        querySnapshot.forEach((doc) => {
          userEmails.push(doc.data().email);
        });
      })
      .catch((error) => {
        console.log("Error getting documents: ", error);
      });

    return userEmails;
  };

  const approveLocations = async () => {
    try {
      const currentUser = firebase.auth().currentUser;
      if (!currentUser) throw new Error("Authentication required");

      const userDoc = await db.collection("users").doc(currentUser.uid).get();
      const userData = userDoc.data();

      if (!userData?.admin) throw new Error("Insufficient permissions");
      if (!selected.length || selected.length > 30)
        throw new Error("Invalid selection count");

      setLoading("approve");
      setSubmitted();

      await db.runTransaction(async (transaction) => {
        const locationRefs = selected.map((id) =>
          db.collection("locations").doc(id)
        );
        const docs = await Promise.all(
          locationRefs.map((ref) => transaction.get(ref))
        );

        if (docs.some((doc) => !doc.exists || doc.data().status !== "pending")) {
          throw new Error("Invalid location selection");
        }

        locationRefs.forEach((ref) => {
          transaction.update(ref, {
            status: "approved",
            approvedBy: currentUser.uid,
            approvedAt: firebase.firestore.FieldValue.serverTimestamp(),
          });
        });
      });

      const emails = await getUserEmails(selected);
      const batch = db.batch();

      emails.forEach((email) => {
        batch.set(db.collection("emails").doc(), {
          to: [email],
          message: {
            subject: "Your HealthDesk Listing Has Been Approved",
            html: `<p>Your HealthDesk listing has been approved and is now visible for patients to see on the map.</p>
            <p>You can update the current estimated waiting time for your clinic and more from the <a href="https://${window.location.host}/dashboard">HealthDesk dashboard</a>.</p>
            <p>Best, <br />
            The HealthDesk Team</p>`,
          },
        });
      });

      await batch.commit();
      setRows((prev) => prev.filter((row) => !selected.includes(row.id)));
      setSelected([]);
      setSubmitted("The selected locations have been approved.");
    } catch (error) {
      console.error("Approval error:", error);
      setLocationError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const rejectLocations = async () => {
    if (!selected.length) return;

    setLoading("reject");
    setSubmitted();

    try {
      const batch = db.batch();

      selected.forEach((id) => {
        batch.set(
          db.collection("locations").doc(id),
          { status: "rejected" },
          { merge: true }
        );
      });

      const emails = await getUserEmails(selected);

      emails.forEach((email) => {
        batch.set(db.collection("emails").doc(), {
          to: [email],
          message: {
            subject: "Your My HealthDesk Listing Has Been Rejected",
            html: `<p>Your My HealthDesk listing has been rejected and will not be visible for patients to see on the map.</p><p>If you think this decision has been made in error, please contact us at support@hlthdsk.com.</p>`,
          },
        });
      });

      await batch.commit();
      setRows((prev) => prev.filter((row) => !selected.includes(row.id)));
      setSelected([]);
      setSubmitted("The selected locations have been rejected.");
    } catch (error) {
      console.error("Rejection error:", error);
      setLocationError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const reviewClaims = async (decision) => {
    if (!selectedClaims.length) return;

    setClaimLoading(decision);
    setClaimError();
    setSubmitted();

    try {
      const reviewFacilityClaim = firebase
        .functions()
        .httpsCallable("reviewFacilityClaim");

      await Promise.all(
        selectedClaims.map((claimId) =>
          reviewFacilityClaim({ claimId, decision })
        )
      );

      setClaims((prev) =>
        prev.filter((claim) => !selectedClaims.includes(claim.id))
      );
      setSelectedClaims([]);
      setSubmitted(
        decision === "approve"
          ? "The selected access requests have been approved."
          : "The selected access requests have been rejected."
      );
    } catch (err) {
      setClaimError(err.message);
    } finally {
      setClaimLoading(false);
    }
  };

  // Cap of 30 due to Firebase `in` query limit
  useEffect(() => {
    if (selected.length > 30) {
      setLocationError("A maximum of 30 locations can be approved at once.");
    } else if (locationError) {
      setLocationError();
    }
  }, [selected]); // eslint-disable-line react-hooks/exhaustive-deps

  const confirmConfig = {
    "location-approve": {
      title: "Approve selected locations?",
      message: `Approve ${selected.length} location${
        selected.length === 1 ? "" : "s"
      }? They will become visible to patients on the map and the owners will be notified by email.`,
      confirmLabel: "Approve locations",
      confirmColor: "primary",
      onConfirm: approveLocations,
    },
    "location-reject": {
      title: "Reject selected locations?",
      message: `Reject ${selected.length} location${
        selected.length === 1 ? "" : "s"
      }? They will not appear on the map and the owners will be notified by email.`,
      confirmLabel: "Reject locations",
      confirmColor: "error",
      onConfirm: rejectLocations,
    },
    "claim-approve": {
      title: "Approve access requests?",
      message: `Approve ${selectedClaims.length} request${
        selectedClaims.length === 1 ? "" : "s"
      }? This grants each person staff access to that facility's patient data. Only approve people you have confirmed belong there.`,
      confirmLabel: "Grant access",
      confirmColor: "primary",
      onConfirm: () => reviewClaims("approve"),
    },
    "claim-reject": {
      title: "Reject access requests?",
      message: `Reject ${selectedClaims.length} request${
        selectedClaims.length === 1 ? "" : "s"
      }?`,
      confirmLabel: "Reject requests",
      confirmColor: "error",
      onConfirm: () => reviewClaims("reject"),
    },
  };

  const activeConfirm = confirm ? confirmConfig[confirm.kind] : null;
  const confirmBusy = !!loading || !!claimLoading;

  const handleConfirm = async () => {
    if (!activeConfirm) return;
    await activeConfirm.onConfirm();
    setConfirm(null);
  };

  if (userData && !userData?.admin) {
    navigate("/dashboard");
    return null;
  }

  if (!userData?.admin) return null;

  return (
    <Box>
      <DashboardPageHeader
        title="Approval"
        subtitle="Review pending clinic listings and facility access requests."
      />

      {submitted ? (
        <Alert severity="success" onClose={() => setSubmitted()} sx={{ mb: 3 }}>
          {submitted}
        </Alert>
      ) : null}

      <DashboardPageHeader
        title="Pending Clinic Locations"
        titleComponent="h3"
        subtitle="Approve Urgent Care locations and allow them to appear on the map."
        actions={
          rows.length > 0 ? (
            <ApprovalActions
              loading={loading}
              disabled={!selected.length || !!locationError}
              approveLabel="Approve"
              rejectLabel="Reject"
              onApprove={() => setConfirm({ kind: "location-approve" })}
              onReject={() => setConfirm({ kind: "location-reject" })}
            />
          ) : null
        }
      />

      {!dataLoaded ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : rows.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="pending clinic locations">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={
                        selected.length > 0 && selected.length < rows.length
                      }
                      checked={
                        rows.length > 0 && selected.length === rows.length
                      }
                      onChange={handleSelectAllLocations}
                      inputProps={{ "aria-label": "select all locations" }}
                    />
                  </TableCell>
                  <TableCell>Clinic Name</TableCell>
                  <TableCell>Address</TableCell>
                  <TableCell>City</TableCell>
                  <TableCell>State</TableCell>
                  <TableCell>ZIP</TableCell>
                  <TableCell>Map</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Registered</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    selected={selected.includes(row.id)}
                    onClick={() => toggleLocation(row.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={selected.includes(row.id)}
                      />
                    </TableCell>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>{row.address}</TableCell>
                    <TableCell>{row.city}</TableCell>
                    <TableCell>{row.state}</TableCell>
                    <TableCell>{row.zip}</TableCell>
                    <TableCell>
                      <Link
                        href={`https://www.google.com/maps/place/${row.lat},${row.lng}`}
                        target="_blank"
                        rel="noopener"
                        onClick={(event) => event.stopPropagation()}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.5,
                        }}
                      >
                        View
                        <OpenInNewIcon fontSize="inherit" />
                      </Link>
                    </TableCell>
                    <TableCell>{row.email || "No email"}</TableCell>
                    <TableCell>{formatTimestamp(row.registrationDate)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {locationError && (
            <FormHelperText error sx={{ mt: 1.5 }}>
              {locationError}
            </FormHelperText>
          )}
        </>
      ) : (
        <Alert severity="info">You're all caught up!</Alert>
      )}

      <DashboardPageHeader
        title="Facility Access Requests"
        titleComponent="h3"
        subtitle="People requesting staff access to an existing facility. Approving grants access to that facility's patient data, so confirm they belong there first."
        sx={{ mt: { xs: 6, sm: 9 } }}
        actions={
          claims.length > 0 ? (
            <ApprovalActions
              loading={claimLoading}
              disabled={!selectedClaims.length}
              approveLabel="Grant access"
              rejectLabel="Reject"
              onApprove={() => setConfirm({ kind: "claim-approve" })}
              onReject={() => setConfirm({ kind: "claim-reject" })}
            />
          ) : null
        }
      />

      {!claimsLoaded ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : claims.length > 0 ? (
        <>
          <TableContainer component={Paper}>
            <Table size="small" aria-label="facility access requests">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={
                        selectedClaims.length > 0 &&
                        selectedClaims.length < claims.length
                      }
                      checked={
                        claims.length > 0 &&
                        selectedClaims.length === claims.length
                      }
                      onChange={handleSelectAllClaims}
                      inputProps={{ "aria-label": "select all requests" }}
                    />
                  </TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Facility</TableCell>
                  <TableCell>Requested</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {claims.map((claim) => (
                  <TableRow
                    key={claim.id}
                    hover
                    selected={selectedClaims.includes(claim.id)}
                    onClick={() => toggleClaim(claim.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        color="primary"
                        checked={selectedClaims.includes(claim.id)}
                      />
                    </TableCell>
                    <TableCell>{claim.userEmail || "—"}</TableCell>
                    <TableCell>{claim.locationName || claim.locationId}</TableCell>
                    <TableCell>{formatTimestamp(claim.createdAt)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {claimError && (
            <FormHelperText error sx={{ mt: 1.5 }}>
              {claimError}
            </FormHelperText>
          )}
        </>
      ) : (
        <Alert severity="info">No pending facility access requests.</Alert>
      )}

      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        title={activeConfirm?.title}
        message={activeConfirm?.message}
        confirmLabel={activeConfirm?.confirmLabel}
        confirmColor={activeConfirm?.confirmColor}
        onConfirm={handleConfirm}
        loading={confirmBusy}
      />
    </Box>
  );
};

export default ApprovalPage;
